import React, { useState, useRef } from "react";
import {
  Bell, ChevronDown
} from "lucide-react";
import NotificationDropdown from "./NotificationDropdown";
import { useNotification } from "../contexts/NotificationContext";
import { DEFAULT_THEME } from "../constants/theme";
import AppBadge from "./ui/AppBadge";
import AppButton from "./ui/AppButton";


export default function Header({ activeMenu, setIsMobileMenuOpen, user, onLogoClick, headerAction, centerAction, selectedDepartment, setSelectedDepartment, myDepartments, deptName, theme, onNavigate, onOpenMemberDetail }) {
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationBtnRef = useRef(null);

  // Get unread count from context
  const { unreadCount } = useNotification();

  // Default theme fallback if not passed (though App.js passes it)
  const activeTheme = theme || DEFAULT_THEME;
  const activeStyle = `${activeTheme.bg} text-white ${activeTheme.border} shadow-sm ${activeTheme.shadow}`;
  const selectedDeptOptionStyle = `${activeTheme.lightBg} ${activeTheme.text}`;
  const getDeptOptionClass = (departmentValue) => (
    `px-3 py-2.5 text-left text-xs font-bold rounded-xl transition-all ${
      selectedDepartment === departmentValue
        ? selectedDeptOptionStyle
        : "text-gray-600 hover:bg-gray-100"
    }`
  );
  const handleDepartmentSelect = (departmentValue) => {
    setSelectedDepartment(departmentValue);
    setIsDeptDropdownOpen(false);
  };

  // [NEW] Check if user has extra departments
  const hasExtraDepts = myDepartments && myDepartments.filter(d => d !== deptName).length > 0;

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between h-12 pl-2 pr-4 bg-[#F0F0EB] md:top-4 md:mx-4 md:mb-4 md:h-12 md:pl-1.5 md:pr-5 md:rounded-[1.5rem] md:border md:border-white/40 md:bg-white/70 md:backdrop-blur-xl md:shadow-lg md:shadow-gray-200/5 md:transition-all">
      <div className="flex items-center gap-4">
        {/* [FIX] 로고 클릭 시 새로고침 없이 이동 */}


        {/* [MOVED] Department Switcher */}
        <div className="relative z-10">

          {/* [MOBILE] Dropdown Style */}
          <div className="md:hidden relative">
            {/* AI Assistant uses centered dropdown, so hide logic here */}
            {activeMenu !== 'ai-assistant' && (
              <>
                {hasExtraDepts ? (
                  <>
                    <button
                      onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
                      className={`flex items-center gap-2 px-3 h-9 rounded-2xl text-xs font-bold border transition-all active:scale-95 whitespace-nowrap ${activeStyle}`}
                    >
                      {selectedDepartment === 'All' ? '전체 부서' : selectedDepartment}
                      <ChevronDown size={14} className={`transition-transform duration-200 ${isDeptDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDeptDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setIsDeptDropdownOpen(false)}></div>
                        <div className="absolute top-full left-0 mt-2 w-48 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/40 z-30 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-left flex flex-col p-1">
                          <button
                            onClick={() => handleDepartmentSelect("All")}
                            className={getDeptOptionClass("All")}
                          >
                            전체 부서
                          </button>
                          {deptName && (
                            <button
                              onClick={() => handleDepartmentSelect(deptName)}
                              className={getDeptOptionClass(deptName)}
                            >
                              {deptName}
                            </button>
                          )}
                          {myDepartments?.filter(d => d !== deptName).map((dept, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleDepartmentSelect(dept)}
                              className={getDeptOptionClass(dept)}
                            >
                              {dept}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  // [NEW] Single Department View (No Dropdown)
                  <div className={`flex items-center gap-2 px-3 h-9 rounded-2xl text-xs font-bold border whitespace-nowrap ${activeStyle}`}>
                    {deptName || selectedDepartment}
                  </div>
                )}
              </>
            )}
          </div>

          {/* [DESKTOP] Pill List Style */}
          <div className="hidden md:flex items-center gap-1.5 overflow-x-auto no-scrollbar max-w-[200px] md:max-w-none mask-fade-right">
            {/* 1. All Departments - Show only if extra depts exist */}
            {hasExtraDepts && (
              <button
                onClick={() => setSelectedDepartment('All')}
                className={`px-3 py-2 rounded-2xl text-xs font-bold transition-all duration-200 whitespace-nowrap active:scale-95 border
                ${selectedDepartment === 'All'
                    ? activeStyle
                    : 'bg-transparent text-gray-500 border-transparent hover:bg-white/50 hover:text-gray-700'
                  }`}
              >
                전체
              </button>
            )}

            {/* 2. Primary Department */}
            {deptName && (
              <button
                onClick={() => setSelectedDepartment(deptName)}
                className={`px-3 py-2 rounded-2xl text-xs font-bold transition-all duration-200 whitespace-nowrap active:scale-95 border
                  ${selectedDepartment === deptName
                    ? activeStyle
                    : 'bg-transparent text-gray-500 border-transparent hover:bg-white/50 hover:text-gray-700'
                  }`}
              >
                {deptName}
              </button>
            )}

            {/* 3. My Departments (Secondary) */}
            {myDepartments?.filter(d => d !== deptName).map((dept, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedDepartment(dept)}
                className={`px-3 py-2 rounded-2xl text-xs font-bold transition-all duration-200 whitespace-nowrap active:scale-95 border
                  ${selectedDepartment === dept
                    ? activeStyle
                    : 'bg-transparent text-gray-500 border-transparent hover:bg-white/50 hover:text-gray-700'
                  }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* [NEW] Center Action (Mobile Only, Absolute Center) */}
      <div className="absolute inset-x-0 bottom-0 top-0 flex items-center justify-center md:hidden z-50 pointer-events-none">
        <div className="pointer-events-auto">
          {centerAction}

          {/* Aaron AI Center Dropdown */}
          {activeMenu === 'ai-assistant' && (
            <div className="relative">
              <button
                onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
                className="flex items-center gap-2 px-3 h-9 rounded-2xl text-[10px] font-bold border border-gray-100 bg-white text-gray-500 shadow-sm whitespace-nowrap active:scale-95 transition-transform"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                <span>{selectedDepartment === 'All' ? '전체 부서 데이터 연동 중' : `${selectedDepartment} 데이터 연동 중`}</span>
                <ChevronDown size={12} className={`ml-0.5 opacity-50 transition-transform duration-200 ${isDeptDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDeptDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDeptDropdownOpen(false)}></div>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-48 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/40 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col p-1">
                    <button
                      onClick={() => handleDepartmentSelect("All")}
                      className={getDeptOptionClass("All")}
                    >
                      전체 부서
                    </button>
                    {deptName && (
                      <button
                        onClick={() => handleDepartmentSelect(deptName)}
                        className={getDeptOptionClass(deptName)}
                      >
                        {deptName}
                      </button>
                    )}
                    {myDepartments?.filter(d => d !== deptName).map((dept, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleDepartmentSelect(dept)}
                        className={getDeptOptionClass(dept)}
                      >
                        {dept}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* [NEW] Header Action (e.g. Settings) */}
        {headerAction}

        {/* Notification / Recent Activity */}
        {activeMenu !== 'ai-assistant' && (
          <div className="relative">
            <AppButton
              ref={notificationBtnRef}
              onClick={() => setShowNotifications(!showNotifications)}
              variant="icon"
              size="icon"
              className="rounded-full relative"
            >
              <Bell size={20} />
              {/* Badge indicator - Only show if unread exists */}
              {unreadCount > 0 && (
                <AppBadge variant="danger" size="xs" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center border-2 border-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </AppBadge>
              )}
            </AppButton>

            {/* Notification Dropdown */}
            {showNotifications && (
              <NotificationDropdown
                onClose={() => setShowNotifications(false)}
                anchorRef={notificationBtnRef}
                onNavigate={onNavigate}
                onOpenMemberDetail={onOpenMemberDetail}
              />
            )}
          </div>
        )}
      </div>
    </header >
  );
}
